/**
 * Responsive Hero Background Image
 *
 * Swaps a hero section's CSS background-image between a smaller mobile
 * rendition and the full-size desktop rendition based on viewport width,
 * using a CSS custom property so any gradient overlay baked into the
 * existing `background-image` value is preserved untouched.
 * Force a rebuild of the vite assets when this file changes.
 */

interface HeroElement extends HTMLElement {
  dataset: {
    heroMobile?: string;
    heroDesktop?: string;
  };
}

const MOBILE_BREAKPOINT = 768; // Tailwind md breakpoint

/**
 * Updates the --hero-bg-image custom property of a hero element based on
 * whether the mobile breakpoint media query currently matches.
 */
function updateHeroBackground(el: HeroElement, isMobile: boolean): void {
  const { heroMobile, heroDesktop } = el.dataset;

  if (!heroMobile || !heroDesktop) return;

  const newUrl = isMobile ? heroMobile : heroDesktop;

  el.style.setProperty('--hero-bg-image', `url('${newUrl}')`);
}

/**
 * Initializes responsive background handling for all hero elements on the page
 */
function initResponsiveHeroes(): void {
  const heroElements = document.querySelectorAll<HeroElement>('[data-hero-mobile][data-hero-desktop]');

  if (heroElements.length === 0) return;

  // Use matchMedia rather than a resize listener: it only fires when the
  // breakpoint is actually crossed, avoiding spurious re-fetches of the other
  // rendition from transient resize events (e.g. viewport emulation quirks).
  const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);

  heroElements.forEach((hero) => {
    updateHeroBackground(hero, mediaQuery.matches);
  });

  mediaQuery.addEventListener('change', (event) => {
    heroElements.forEach((hero) => updateHeroBackground(hero, event.matches));
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initResponsiveHeroes);
} else {
  // DOM is already ready
  initResponsiveHeroes();
}

export { initResponsiveHeroes, updateHeroBackground };
